import { zodResolver } from "@hookform/resolvers/zod";
import merge from "lodash.merge";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { LoanDetailsController } from "services/loanDetails/controller";
import { FormRenderer, useAccordionWizard } from "stories";
import { AccordionForm } from "stories/components/accordionForm";
import { arrayRange } from "utils/ArrayRange";
import { useFileUpload } from "utils/customHooks/useFileUpload";
import { useToast } from "utils/customHooks/useToast";
import { z } from "zod";
import { getGuarantorModel, guarantors } from ".";
import { removeEmptyValues } from "../borrower/LoanDetailsBorrower";

const loanDetailsController = LoanDetailsController();
export const GuarantorsQueryKey = "get-additional-guarantors";

const schema = z
  .object({
    guarantors: z.array(
      z
        .object({
          name: z.string().min(1, "This field is required"),
          corporate_structure: z.string().min(1, "This field is required."),
        })
        .passthrough()
    ),
  })
  .passthrough();
const LoanDetailsGuarantors = () => {
  const params = useParams();
  const queryKey = [GuarantorsQueryKey, { id: params.id }];
  const { next } = useAccordionWizard();
  const { showToast } = useToast();
  const { handleFileFields } = useFileUpload();
  const queryClient = useQueryClient();
  const form = useForm({
    defaultValues: {
      guarantors: [
        merge(getGuarantorModel(), {
          partners: [{ opportunity_id: parseInt(params.id) }],
        }),
      ],
    },
    resolver: zodResolver(schema),
  });

  const { isLoading: fetchLoading } = useQuery({
    queryKey,
    queryFn: () => loanDetailsController.getAdditionalGuarantors(params.id),
    onSuccess: (data) => {
      const defaultValues = arrayRange(Math.max(data.length, 1)).map(() =>
        getGuarantorModel()
      );
      let guarantors = merge(defaultValues, data);
      form.reset({ guarantors });
    },
  });

  const handleGuarantorSubmit = async (values) => {
    values = removeEmptyValues(values);
    return handleFileFields(values).then((values) => {
      values.opportunityId = params.id;
      return loanDetailsController.updateAdditionalGuarantors(values);
    });
  };

  const { mutate, isLoading: postLoading } = useMutation(
    handleGuarantorSubmit,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
        showToast("Additional Guarantors Updated!", "success");
        next();
      },
    }
  );

  return (
    <AccordionForm
      accordionKey="additional-guarantors"
      accordionTitle="Additional Guarantors"
      form={form}
      isPostLoading={postLoading}
      isPreLoading={fetchLoading}
      onSubmit={mutate}
    >
      <FormRenderer fields={guarantors} />
    </AccordionForm>
  );
};

export default LoanDetailsGuarantors;
